
import { MealComposition, MealCompositionWithRecipes, DbMealComposition } from "@/types/mealComposition";
import { Recipe } from "@/types/recipe";

/**
 * Convert a MealComposition entity to database format for insertion
 */
export const formatMealCompositionForDbInsert = (
  composition: Omit<MealComposition, 'id' | 'createdAt' | 'updatedAt' | 'userId' | 'familyId'>,
  userId: string,
  familyId: string
): Omit<DbMealComposition, 'id' | 'created_at' | 'updated_at'> => {
  return {
    name: composition.name,
    recipe_ids: composition.recipeIds,
    tags: composition.tags || [],
    user_id: userId,
    family_id: familyId,
  };
};

/**
 * Convert partial MealComposition updates to database format for updates
 */
export const formatMealCompositionForDbUpdate = (
  updates: Partial<MealComposition>,
  userId: string
): Partial<Omit<DbMealComposition, 'id' | 'created_at'>> => {
  const dbUpdates: Partial<Omit<DbMealComposition, 'id' | 'created_at'>> = {};
  
  if (updates.name !== undefined) {
    dbUpdates.name = updates.name;
  }
  
  if (updates.recipeIds !== undefined) {
    dbUpdates.recipe_ids = updates.recipeIds;
  }

  if (updates.tags !== undefined) {
    dbUpdates.tags = updates.tags;
  }
  
  if (updates.familyId !== undefined) {
    dbUpdates.family_id = updates.familyId;
  }
  
  // Always include user_id and updated_at for security and tracking
  dbUpdates.user_id = userId;
  dbUpdates.updated_at = new Date().toISOString();
  
  return dbUpdates;
};

/**
 * Convert a database MealComposition to the application format
 */
export const mapDbMealCompositionToMealComposition = (dbComposition: DbMealComposition): MealComposition => {
  return {
    id: dbComposition.id,
    name: dbComposition.name,
    recipeIds: dbComposition.recipe_ids,
    tags: dbComposition.tags || [],
    familyId: dbComposition.family_id,
    userId: dbComposition.user_id,
    createdAt: dbComposition.created_at,
    updatedAt: dbComposition.updated_at,
  };
};

/**
 * Populate a MealComposition with its recipe details
 */
export const populateCompositionWithRecipes = (
  composition: MealComposition,
  allRecipes: Recipe[]
): MealCompositionWithRecipes => {
  const recipes = composition.recipeIds
    .map(recipeId => allRecipes.find(recipe => recipe.id === recipeId))
    .filter((recipe): recipe is Recipe => recipe !== undefined);

  return {
    ...composition,
    recipes,
  };
};
